/**
 * V2CanvasView.jsx — embeddable V2 hierarchical-DAG renderer.
 *
 * Pulls the visualisation pipeline out of `pages/CanvasPageV2.jsx` so it
 * can be dropped into any host (legacy /canvas chrome, the standalone
 * /canvas-v2 demo harness, future preview surfaces) by passing
 * `{nodes, connections}` and a small set of callbacks. The host owns the
 * payload + dirty/save plumbing; this component just lays out and renders.
 *
 * Built for /canvas-v3 (#182): combines the legacy left/top/right chrome
 * with V2's ReactFlow + ELK rendering.
 *
 * Capabilities preserved from the standalone V2 page:
 *   - Hierarchical ELK layout via flatToElkHierarchical / elkToReactFlow
 *   - SubWorkflow inline drill-in (#168) — click a registered subwf
 *   - ⌘K command palette (#171) — fuzzy node jump with flash pulse
 *   - Per-task execution overlay — pass an executionState Map
 *   - Diff overlay (#170) — pass a diffStatus map keyed by source-node id
 *
 * What this component DOES NOT own:
 *   - Workflow IO (load/save) — host calls services/api directly
 *   - URL routing — host reads ?workflowId / ?runId / ?executionId
 *   - Property panel — host renders its own (legacy NodePropertiesPanel,
 *     V2 NodeConfigPanel, or a future combined editor)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  ControlButton,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from '@xyflow/react';
import { Plus } from 'lucide-react';
import '@xyflow/react/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';
import { flatToElkHierarchical, cognitiveCatalogId } from '../../utils/v2/flatToElkHierarchical';
import { elkToReactFlow } from '../../utils/v2/elkToReactFlow';
import { lintCanvas, diagnosticsByNodeId, PAYLOAD_NODE_ID, SEVERITY } from '../../utils/v2/v2Lint';
import { V2_NODE_TYPES } from './nodes/V2Nodes';
import V2Edge from './edges/V2Edge';
import { V2_PAL, V2_STONE } from './nodes/v2Palette';
import { lookupChildCanvas } from './childWorkflowRegistry';
import CommandPalette from './CommandPalette';

const V2_EDGE_TYPES = { v2Edge: V2Edge };

let _hierElk = null;
function getHierElk() {
  if (!_hierElk) _hierElk = new ELK();
  return _hierElk;
}

// Inject `_embeddedCanvas` on subworkflow nodes that the host has marked
// as expanded — same contract as standalone /canvas-v2.
function injectExpandedChildren(payload, expandedSet) {
  if (!payload || !Array.isArray(payload.nodes) || !expandedSet || expandedSet.size === 0) {
    return payload;
  }
  let mutated = false;
  const nextNodes = payload.nodes.map((n) => {
    if (!n || !n.id || !expandedSet.has(n.id)) return n;
    if (n.type !== 'sub_workflow' && n.type !== 'async_sub_workflow') return n;
    const cfg = n.config || {};
    if (cfg._embeddedCanvas) return n;
    const childCanvas = lookupChildCanvas(cfg.subWorkflowName || cfg.name, cfg.subWorkflowVersion, n.label);
    if (!childCanvas) return n;
    mutated = true;
    return { ...n, config: { ...cfg, _embeddedCanvas: childCanvas } };
  });
  if (!mutated) return payload;
  return { ...payload, nodes: nextNodes };
}

async function layoutHierarchical(payload) {
  const nodes = Array.isArray(payload?.nodes) ? payload.nodes : [];
  const connections = Array.isArray(payload?.connections) ? payload.connections : [];
  if (nodes.length === 0) return { nodes: [], edges: [], error: null };
  try {
    const graph = flatToElkHierarchical({ nodes, connections });
    const laid = await getHierElk().layout(graph);
    const { rfNodes, rfEdges } = elkToReactFlow(laid);

    // ── W133: Disconnected node rendering ─────────────────────────────
    // flatToIR walks from Start and only emits nodes reachable from the
    // entry sentinel — orphan canvas nodes (no incoming/outgoing edge to
    // the main flow) get silently dropped from the rendered DAG, even
    // though they're present in payload.nodes and visible in the JSON
    // Code panel. That confused users with the bug "I added a Human Task
    // and the Code shows it but the canvas doesn't" (#133).
    //
    // To honour the user's intent — every node in canvas state should
    // be visible — we append disconnected nodes to a "Disconnected"
    // zone to the right of the main DAG. They render as standard leaf
    // cards with a `disconnected: true` flag in their data so the leaf
    // renderer can show a subtle badge if it wants to. Drag/drop and
    // edge connection still work normally; the user can wire them in
    // by drawing an edge from any neighbour, or delete them.
    const emittedSourceIds = new Set();
    for (const n of rfNodes) {
      const sid = n && n.data && n.data.sourceNodeId;
      if (sid) emittedSourceIds.add(sid);
    }

    /* G267 — Cognitive tool catalog is NOT an orphan set.
     *
     * flatToIR deliberately keeps cognitive fan-out skills OFF the structural trunk (the LLM
     * chooses them at runtime; they are not a sequence). The W133 orphan check reuses that
     * same trunk walk, so it concluded "unreachable" and painted the agent's own tool catalog
     * as DISCONNECTED / WARN errors. Two different questions were conflated:
     *   "is this node on the trunk?"   → correctly No.
     *   "is this node legitimate?"     → Yes — it's reachable via a `cognitive_avail` edge.
     * Tools reached from a cognitive_loop are first-class members of the loop's tool catalog
     * (AGENT_EXECUTION_MODEL §4). Flag them as such so the renderer shows "may invoke" rather
     * than an error badge. Genuine orphans (no edge at all) still surface as before. */
    const cognitiveToolIds = new Set();
    for (const c of connections) {
      const handle = c && (c.sourceHandle ?? c.fromPort);
      const target = c && (c.target ?? c.to);
      if (handle === 'cognitive_avail' && target) cognitiveToolIds.add(target);
    }

    const orphans = nodes.filter((cn) => cn && cn.id && !emittedSourceIds.has(cn.id));
    if (orphans.length > 0) {
      // Right edge of the main DAG, with a 120 px gutter, becomes the
      // anchor for the Disconnected column. Orphans stack vertically
      // 32 px apart so several visible at once.
      let rightEdge = 0;
      for (const n of rfNodes) {
        const x = (n && n.position && n.position.x) || 0;
        const w = (n && n.width) || 0;
        if (x + w > rightEdge) rightEdge = x + w;
      }
      const baseX = rightEdge + 120;
      const baseY = 40;
      const stride = 100;

      /* G267c — TOOL CATALOG = a dotted GROUP placed clear of the whole DAG.
       *
       * Two earlier placements both failed, for the same underlying reason:
       *   (a) far-off Disconnected column → "may invoke" edges slashed diagonally across
       *       the canvas;
       *   (b) hugging the loop (loop.x + loop.width + gutter) → the catalog sat INSIDE the
       *       horizontal span of the governance fan above it, so the routes ran straight
       *       through the JOIN and the policy cards. That's the "arrow coming out of the
       *       join" in the screenshot: it was never attached to the JOIN, it was the
       *       catalog route passing through at exactly the JOIN's y.
       *
       * Anchoring at `rightEdge + gutter` — the right edge of EVERY laid-out node — makes
       * overlap structurally impossible rather than merely unlikely: there is nothing to the
       * right of the graph to paint over. Vertically we centre the group on its loop, so the
       * single loop → catalog edge is a short level run.
       *
       * Grouping is keyed by LOOP (mirrors the cross-cutting edge emission in
       * flatToElkHierarchical), so a hybrid agent with more than one cognitive block gets
       * one catalog each rather than a single merged pile. */
      const CAT_PAD_X = 18;
      const CAT_HEAD = 30;      // room for the "MAY INVOKE · LLM DECIDES" chip
      const CAT_PAD_B = 18;
      const TOOL_W = 260;       // must match V2Leaf's fixed card width
      const TOOL_H = 76;
      const TOOL_GAP = 20;
      const CAT_W = TOOL_W + CAT_PAD_X * 2;

      /* loop canvas id → tool canvas ids (only loops that actually have tools). */
      const toolsByLoop = new Map();
      for (const c of connections) {
        const handle = c && (c.sourceHandle ?? c.fromPort);
        const src = c && (c.source ?? c.from);
        const tgt = c && (c.target ?? c.to);
        if (!src || !tgt) continue;
        const isAvail = handle === 'cognitive_avail';
        const isReturn = handle === 'cognitive_return';
        if (!isAvail && !isReturn) continue;
        const loopId = isAvail ? src : tgt;
        const toolId = isAvail ? tgt : src;
        if (!toolsByLoop.has(loopId)) toolsByLoop.set(loopId, new Set());
        toolsByLoop.get(loopId).add(toolId);
      }

      /* Geometry per catalog, computed BEFORE any node is pushed so the tools can be
       * positioned inside their group. `toolSlot` maps tool id → absolute position. */
      const toolSlot = new Map();
      const catalogNodes = [];
      const orphanIds = new Set(orphans.map((cn) => cn.id));
      let catalogRight = baseX;
      for (const [loopId, toolIdSet] of toolsByLoop) {
        // Only tools that were actually pinned as orphans belong in the group.
        const toolIds = [...toolIdSet].filter((tid) => orphanIds.has(tid));
        if (toolIds.length === 0) continue;
        const loopRf = rfNodes.find((n) => n?.data?.sourceNodeId === loopId);
        const groupH = CAT_HEAD + toolIds.length * TOOL_H
                     + (toolIds.length - 1) * TOOL_GAP + CAT_PAD_B;
        const loopCenterY = loopRf
          ? (loopRf.position?.y ?? 0) + ((loopRf.height ?? 72) / 2)
          : baseY + groupH / 2;
        const gx = baseX;
        const gy = loopCenterY - groupH / 2;
        toolIds.forEach((tid, i) => {
          toolSlot.set(tid, {
            x: gx + CAT_PAD_X,
            y: gy + CAT_HEAD + i * (TOOL_H + TOOL_GAP),
          });
        });
        catalogNodes.push({
          id: cognitiveCatalogId(loopId),
          type: 'v2ToolCatalog',
          position: { x: gx, y: gy },
          width: CAT_W,
          height: groupH,
          style: { width: CAT_W, height: groupH },
          data: { kind: 'toolCatalog', toolCount: toolIds.length, loopId },
          draggable: false,
          selectable: false,
          /* Chrome sits BEHIND its tool cards. Explicit zIndex rather than relying on array
           * order, because the orphan cards are pushed in a separate pass below. */
          zIndex: -1,
        });
        catalogRight = Math.max(catalogRight, gx + CAT_W);
      }
      rfNodes.push(...catalogNodes);

      /* Genuine orphans (no edge at all) keep their Disconnected column — but move it clear
       * of the catalog so the two never collide. */
      const orphanX = catalogNodes.length > 0 ? catalogRight + 120 : baseX;
      let plainIdx = 0;

      orphans.forEach((cn) => {
        // G267 — Use the canvas node id directly (not 'orphan_'+cn.id) so
        // cross-cutting edges emitted by flatToElkHierarchical (cognitive
        // availability/return edges) resolve to this orphan rfNode by id.
        // Collision impossible: by construction cn.id is not in
        // emittedSourceIds, so no laid-out rfNode shares the id.
        const isCognitiveTool = cognitiveToolIds.has(cn.id);
        const slot = toolSlot.get(cn.id);
        // Tool catalog cards sit inside their group; genuine orphans keep the column.
        const pos = (isCognitiveTool && slot)
          ? slot
          : { x: orphanX, y: baseY + (plainIdx++) * stride };
        rfNodes.push({
          id: cn.id,
          type: 'v2Leaf',
          /* 240×64 understated the card: V2Leaf renders at a fixed 260 wide / 72 min-height,
           * so the declared box was smaller than the paint and the catalog group would have
           * been sized to clip its own cards. Declare the real footprint. */
          position: pos,
          width: TOOL_W,
          height: TOOL_H,
          style: { width: TOOL_W, height: TOOL_H },
          data: {
            kind: 'leaf',
            label: cn.label || cn.type || cn.id,
            canvasType: cn.type || 'task',
            sourceNodeId: cn.id,
            props: cn.config || {},
            /* A cognitive tool is reachable BY DESIGN via its availability edge — it is not
             * a disconnected error. Flag it so the leaf renderer shows a "may invoke" tool
             * badge (and its governance posture) instead of DISCONNECTED / WARN. */
            disconnected: !isCognitiveTool,
            cognitiveTool: isCognitiveTool,
            autonomy: isCognitiveTool ? (cn.config?.autonomy || null) : null,
            /* Human-gate posture. Prefer the flag the materialiser stamps directly
             * (config.approvalRequired); fall back to the raw guardrails blob for CEGs
             * materialised before that stamp existed, and to a HUMAN_CONTROLLED autonomy —
             * any of those means this tool cannot fire without a human. */
            approvalRequired: isCognitiveTool
              ? !!(cn.config?.approvalRequired
                   || cn.config?.guardrails?.approval_required
                   || cn.config?.guardrails?.approvalRequired
                   || String(cn.config?.autonomy || '').toUpperCase() === 'HUMAN_CONTROLLED')
              : false,
          },
          draggable: true,
          selectable: true,
        });
      });
    }

    return { nodes: rfNodes, edges: rfEdges, error: null };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[V2CanvasView] hierarchical layout failed', err);
    return { nodes: [], edges: [], error: err?.message || String(err) };
  }
}

function V2CanvasViewInner({
  payload,
  executionState,        // Map<nodeId, {status, durationMs, ...}> | null
  selectedNodeId,        // host's selected node id (canvas-shape id, not rf id)
  onSelectNode,          // (sourceNodeId | null) => void  — single-click select
  onNodeDoubleClick,     // (sourceNodeId) => void          — double-click trigger (e.g. AI Explainer)
  onPayloadChange,       // (nextPayload) => void  — fires when drill-in changes the payload via injection
  // Palette drop callback. Fires when the user drops a palette
  // dataTransfer payload on the ReactFlow surface. We do all the
  // coord/edge-hit math inside this component (where useReactFlow's
  // screenToFlowPosition + the laid-out rfNodes are in scope) and
  // pass results up — the host decides whether to insertOnConnection
  // or addNode based on whether matchedConnectionId is non-null.
  //
  // Args: { nodeType, matchedConnectionId | null, flowPosition: {x, y} }
  onPaletteDrop,
  // Optional: opens the global Task Picker in palette mode. When provided,
  // we render a leading "+" button on the bottom Controls toolbar so users
  // who don't know about ⌘E (or are on a touch device) have a discoverable
  // entry point. No-op fallback if the host doesn't pass it.
  onOpenPicker,
  // Optional: opens the global Task Picker anchored to a specific node
  // ("insert below this one"). Wired into each task card's bottom "+"
  // button via data decoration. Signature: (sourceNodeId: string) => void.
  // When omitted (e.g. /canvas-v2 read-only viewer), the per-task "+" is
  // suppressed so the canvas stays read-only.
  onAddAfter,
  // Canvas Round 1 Patch A — persists user-drag of a node back to canvas
  // state. Signature: ({ sourceNodeId, x, y }) => void. Without this,
  // dragging is purely visual (rfNodes updates locally via onNodesChange
  // but the host's canvas state never learns), and the next layout pass
  // snaps the node back to its computed position. Optional: hosts that
  // don't want drag persistence (read-only /canvas-v2 viewer, locked
  // AutoLet CEG) simply omit the prop.
  onNodeMove,
  // Canvas Round 1 Patch B — restores manual edge drawing. ReactFlow
  // fires `onConnect` when the user drags from one node's handle to
  // another. We resolve the rfNode ids (which may differ from canvas
  // ids — ELK can insert routing nodes) back to canvas sourceNodeIds
  // before handing up. Signature:
  //   ({ sourceNodeId, targetNodeId, sourceHandle, targetHandle }) => void
  // Optional: hosts that don't want manual edge creation (read-only
  // viewer, locked canvas) simply omit the prop. The host typically
  // calls canvas.addConnection; if it returns null the connection was
  // rejected by canConnect (cycle, type mismatch, duplicate) and the
  // host should surface a toast.
  onConnectEdge,
  diffMap,               // optional Map<sourceId, 'added'|'removed'|'modified'>
  showLintBadges = true,
  showMinimap = true,
  // RC-130 — when the host wants the canvas to pan/zoom to a specific
  // node (e.g. the Canvas Issues panel's "Focus on canvas" buttons),
  // it passes a `focusTarget` object of shape `{ nodeId, key }`.
  // The `key` field is a monotonically-increasing counter that the
  // host bumps on every focus request so the same nodeId can be
  // focused twice in a row and the useEffect dep changes both times.
  // Internal useEffect watches focusTarget identity and calls
  // ReactFlow.fitView({ nodes: [<matched rfNode>], padding, duration })
  // to pan + zoom into view, then briefly flashes the node so the
  // operator can visually confirm what they just navigated to.
  focusTarget = null,
}) {
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  // Connection id (canvas-shape) currently under the dragging cursor.
  // When non-null we add a highlight to the matching ReactFlow edge so
  // the user gets visual feedback that the drop will split that edge.
  const [hoveredEdgeId, setHoveredEdgeId] = useState(null);
  // ReactFlow hooks are valid here — V2CanvasView wraps the inner
  // component in ReactFlowProvider. screenToFlowPosition gives us the
  // pointer location in the same coordinate system the laid-out
  // rfNodes use, so the proximity math actually compares apples to
  // apples (which is what's broken when the drop handler lived on the
  // outer SVG-world wrapper in CanvasPageV3).
  const reactFlow = useReactFlow();
  const [layoutError, setLayoutError] = useState(null);
  const [, setIsLaying] = useState(false);
  const [expandedSubworkflows, setExpandedSubworkflows] = useState(() => new Set());
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [flashRfId, setFlashRfId] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Re-layout whenever the payload, expansion set, or diff stamping
  // changes. Lint decoration runs in the same pass so per-node
  // diagnostics ride on the rf nodes' data.
  useEffect(() => {
    let cancelled = false;
    const stamped = injectExpandedChildren(payload, expandedSubworkflows);
    setIsLaying(true);
    setLayoutError(null);
    layoutHierarchical(stamped).then(({ nodes, edges, error }) => {
      if (cancelled || !mountedRef.current) return;

      // Lint pass — same as the standalone /canvas-v2.
      let decorated = nodes;
      try {
        if (showLintBadges) {
          const { diagnostics } = lintCanvas(stamped);
          const perNode = diagnosticsByNodeId(diagnostics.filter((d) => d.nodeId !== PAYLOAD_NODE_ID));
          decorated = nodes.map((n) => {
            const srcId = (n.data && n.data.sourceNodeId) || n.id;
            const diags = perNode.get(srcId);
            if (!diags || diags.length === 0) return n;
            return { ...n, data: { ...n.data, diagnostics: diags } };
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[V2CanvasView] lint pass failed', e);
      }

      // Diff stamps (#170) — className tinting via global v2-diff-* CSS.
      if (diffMap && diffMap.size > 0) {
        decorated = decorated.map((n) => {
          const srcId = (n.data && n.data.sourceNodeId) || n.id;
          const status = diffMap.get(srcId);
          if (!status || status === 'unchanged') return n;
          const prev = (n.className || '').replace(/(^|\s)v2-diff-(added|removed|modified)(\s|$)/g, ' ').trim();
          const cls = `v2-diff-${status}`;
          return { ...n, className: prev ? `${prev} ${cls}` : cls, data: { ...n.data, diffStatus: status } };
        });
      }

      // Execution state (#180) — paint exec rings + duration badges.
      if (executionState && executionState.size > 0) {
        decorated = decorated.map((n) => {
          const srcId = (n.data && n.data.sourceNodeId) || n.id;
          const ne = executionState.get(srcId);
          if (!ne) return n;
          return {
            ...n,
            data: {
              ...n.data,
              execStatus: (ne.status || '').toString().toLowerCase() || null,
              execDurationMs: typeof ne.durationMs === 'number' ? ne.durationMs : null,
            },
          };
        });
      }

      // Selection highlight — match by source id.
      if (selectedNodeId) {
        decorated = decorated.map((n) => {
          const srcId = (n.data && n.data.sourceNodeId) || n.id;
          const isSel = srcId === selectedNodeId;
          if (!isSel) return n;
          const prev = (n.className || '').replace(/(^|\s)v2-selected(\s|$)/g, ' ').trim();
          return { ...n, className: prev ? `${prev} v2-selected` : 'v2-selected' };
        });
      }

      // Per-task "+" callback decoration. Only attach to leaf-class
      // tasks — the ones a user thinks of as "a step". Skip switch /
      // fork / join / loop heads and containers (insertion semantics
      // differ — those need to insert into a specific branch / leg,
      // not "after" the construct as a whole), and sub-workflow chrome.
      //
      // Sentinels: only the START sentinel receives the "+" decoration
      // (RC-20c). END never gets one because it is, by definition, the
      // termination point — nothing comes after. The V2Sentinel
      // component itself gates rendering of the "+" on
      // `data.kind === 'start'`, so we pass `onAddAfter` to ALL
      // sentinels and let the component decide; this keeps the
      // type-set narrow + the rendering logic local to V2Sentinel.
      //
      // Node-type names match the keys in V2_NODE_TYPES (V2Nodes.jsx).
      const ADD_AFTER_NODE_TYPES = new Set([
        'v2Leaf',
        'v2Stub',
        'v2Sentinel',
      ]);
      if (typeof onAddAfter === 'function') {
        decorated = decorated.map((n) => {
          if (!ADD_AFTER_NODE_TYPES.has(n.type)) return n;
          return { ...n, data: { ...n.data, onAddAfter } };
        });
      }

      // V169 / Wave 3 follow-up: when the layout pipeline throws (e.g.
      // flatToIR rejects multi-Start, or ELK chokes on a malformed
      // graph), we used to overwrite rfNodes with the empty array
      // returned by layoutHierarchical's catch block — so the user's
      // canvas would go BLANK with only a top-right error banner. They
      // couldn't see what they had, couldn't undo, couldn't drag the
      // offending node back off. Recoverable error → unrecoverable UX.
      //
      // New behaviour: keep the previous layout on screen when the new
      // pass fails, so the user sees their nodes + a clear error
      // explaining what went wrong. They can delete the offending node
      // and the next render pass will succeed cleanly. We still set
      // edges and decoration if the layout produced any (always-empty
      // when error, but defensive for future paths).
      if (error) {
        setLayoutError(error);
        // Don't clobber rfNodes/rfEdges on error — keep the last good
        // layout visible so the user has context for recovery.
      } else {
        setRfNodes(decorated);
        setRfEdges(edges);
        setLayoutError(null);
      }
      setIsLaying(false);

      // If we injected drill-in canvases, let the host know so it can
      // keep its mirror in sync (e.g. for save serialisation we'll want
      // to strip _embeddedCanvas back out — that's handled by the
      // strip helper on the host side).
      if (typeof onPayloadChange === 'function' && stamped !== payload) {
        onPayloadChange(stamped);
      }
    });
    return () => { cancelled = true; };
  }, [payload, expandedSubworkflows, executionState, diffMap, selectedNodeId, onPayloadChange, setRfNodes, setRfEdges, showLintBadges, onAddAfter]);

  // RC-20b (task #1) — Auto fit-view after a workflow first lays out.
  //
  // The ReactFlow `fitView` prop fires once on initial mount with whatever
  // nodes happen to exist at that instant. For V3 that initial set is
  // typically empty (the payload arrives async from /api/v1/workflows/:id
  // *after* the ReactFlow mounts), so the initial fitView is a no-op and
  // the next time nodes appear they land at the default viewport — usually
  // the upper-left, which for our START/END sentinel layout means the
  // sentinels sit just off-screen and the user has to click "Fit View"
  // to see them.
  //
  // Fix: re-run fitView when the rendered node count transitions from
  // 0 to >0 (first paint after load), or when a previously-non-empty
  // canvas drops back to just the sentinels. We intentionally do NOT
  // re-fit on every node add — that would yank the viewport around as
  // the user authors. The `fitViewOptions` we pass match the props
  // configured on the <ReactFlow> below so the auto-fit matches the
  // user-driven fit.
  const lastFitNodeCountRef = useRef(0);
  useEffect(() => {
    const count = rfNodes.length;
    const prev = lastFitNodeCountRef.current;
    // Trigger when transitioning from empty → populated (initial load)
    // OR from many nodes → just sentinels (workflow reset). Stay quiet
    // for all other transitions so the viewport doesn't fight the user.
    const becamePopulated = prev === 0 && count > 0;
    const collapsedToSentinels = prev > 2 && count > 0 && count <= 2;
    if ((becamePopulated || collapsedToSentinels) && reactFlow && typeof reactFlow.fitView === 'function') {
      // Defer to the next paint so ReactFlow has actually applied the
      // new node positions before we measure.
      const handle = requestAnimationFrame(() => {
        try {
          reactFlow.fitView({ padding: 0.2, maxZoom: 1, minZoom: 0.1, duration: 250 });
        } catch (_) { /* ignore — fitView throws if viewport not ready */ }
      });
      lastFitNodeCountRef.current = count;
      return () => cancelAnimationFrame(handle);
    }
    lastFitNodeCountRef.current = count;
    return undefined;
  }, [rfNodes.length, reactFlow]);

  /* RC-130 — Pan + zoom + flash when the host requests a focus.
   *
   * Watches the focusTarget prop and, on each new value (identity
   * change — host bumps the `key` field per request), finds the
   * rendered ReactFlow node whose data.sourceNodeId matches the
   * canvas-shape nodeId the host asked for, then calls fitView on
   * that single node with a comfortable padding so the operator
   * sees the node centered in the viewport. Triggers the existing
   * flash effect at the same time so the operator gets a clear
   * "yes, that's the one" confirmation.
   *
   * Defensive paths:
   *   - No-op when focusTarget is null / has no nodeId
   *   - Defers to next animation frame so any pending layout pass
   *     completes before we measure (matches the existing fit-on-
   *     mount effect at line ~400)
   *   - try/catch around fitView — it throws if viewport not ready
   *   - Cleanup cancels the pending RAF if focusTarget changes
   *     again or the component unmounts during the delay
   *
   * Falls back to a generic fitView if the target node can't be
   * resolved (e.g. the host asked us to focus a node that hasn't
   * rendered yet — rare, but better than doing nothing). */
  useEffect(() => {
    if (!focusTarget || !focusTarget.nodeId) return undefined;
    if (!reactFlow || typeof reactFlow.fitView !== 'function') return undefined;
    const targetCanvasId = focusTarget.nodeId;
    const handle = requestAnimationFrame(() => {
      try {
        // Find the rfNode whose data.sourceNodeId matches the canvas id.
        // (rfNode.id may differ from canvas id when ELK inserts routing
        // nodes; data.sourceNodeId is the stable bridge back to canvas
        // coordinates.) Fall back to direct id match for cases where
        // sourceNodeId is the same as rfNode.id.
        const all = typeof reactFlow.getNodes === 'function' ? reactFlow.getNodes() : [];
        const match = all.find(
          (n) => (n.data && n.data.sourceNodeId === targetCanvasId) || n.id === targetCanvasId
        );
        if (match && match.id) {
          reactFlow.fitView({
            nodes: [{ id: match.id }],
            padding: 0.4,
            duration: 350,
            maxZoom: 1.2,
            minZoom: 0.4,
          });
          // Also briefly flash the node so the operator can visually
          // confirm what they just navigated to.
          setFlashRfId(match.id);
          setTimeout(() => setFlashRfId(null), 1400);
        } else {
          // No render match — fall back to a global fit so at least
          // SOMETHING happens. Better than the pre-RC-130 silent no-op.
          reactFlow.fitView({ padding: 0.2, duration: 300, maxZoom: 1, minZoom: 0.1 });
        }
      } catch (_) { /* fitView throws if viewport not ready — silent */ }
    });
    return () => cancelAnimationFrame(handle);
  }, [focusTarget, reactFlow]);

  // Flash className for palette-jump pulses.
  useEffect(() => {
    setRfNodes((prev) => prev.map((n) => {
      const wantFlash = flashRfId && n.id === flashRfId;
      const hasFlash = n.className && n.className.indexOf('v2-flash') >= 0;
      if (wantFlash && hasFlash) return n;
      if (!wantFlash && !hasFlash) return n;
      const base = (n.className || '').replace(/(^|\s)v2-flash(\s|$)/g, ' ').trim();
      const next = wantFlash ? (base ? `${base} v2-flash` : 'v2-flash') : base;
      return { ...n, className: next || undefined };
    }));
  }, [flashRfId, setRfNodes]);

  // ⌘⇧F (Cmd/Ctrl + Shift + F) — open Jump-to-Node palette.
  //
  // RC-20e (task #1): rebound from ⌘K to ⌘⇧F. ⌘K was conflicting with
  // the platform top-bar global search — pressing ⌘K from anywhere on
  // /canvas opened the canvas palette instead of the platform search.
  // ⌘⇧F ("Find") is a Sublime/VS-Code convention for "find symbol", maps
  // cleanly onto "find a node in this workflow", and is not reserved by
  // any major browser, OS, or other canvas hotkey. ⌘K is now free for
  // the platform top-bar search only.
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta || !e.shiftKey) return;
      if (e.key === 'f' || e.key === 'F') {
        const tgt = e.target;
        const tag = tgt && tgt.tagName ? tgt.tagName.toLowerCase() : '';
        const inEditable =
          tag === 'textarea' ||
          (tag === 'input' && tgt.type !== 'checkbox' && tgt.type !== 'radio') ||
          (tgt && tgt.isContentEditable);
        if (inEditable) return;
        e.preventDefault();
        setCommandPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Click handler — drill-in for SubWorkflows with a registered child,
  // selection for everything else. Host's onSelectNode wires the right
  // panel up to canvas state.
  const handleNodeClick = useCallback((_event, node) => {
    if (!node || !node.type) return;
    const sourceId = node.data && node.data.sourceNodeId;
    if (!sourceId) return;

    const isSubWf = node.type === 'v2SubWorkflow' || node.type === 'v2SubWorkflowHead';
    if (isSubWf) {
      const src = payload && Array.isArray(payload.nodes)
        ? payload.nodes.find((n) => n && n.id === sourceId) : null;
      const cfg = (src && src.config) || {};
      const child = lookupChildCanvas(cfg.subWorkflowName || cfg.name, cfg.subWorkflowVersion, src && src.label);
      if (child) {
        setExpandedSubworkflows((prev) => {
          const next = new Set(prev);
          if (next.has(sourceId)) next.delete(sourceId);
          else next.add(sourceId);
          return next;
        });
        return;
      }
    }

    const SKIP = new Set([
      'v2SwitchContainer', 'v2CaseContainer',
      'v2ForkContainer', 'v2LegContainer', 'v2DynForkContainer',
      'v2LoopContainer', 'v2LoopBody',
      'v2SubWorkflowContainer',
      'v2Meet', 'v2Sentinel',
    ]);
    if (SKIP.has(node.type)) return;
    if (typeof onSelectNode === 'function') onSelectNode(sourceId);
  }, [payload, onSelectNode]);

  const handlePaneClick = useCallback(() => {
    if (typeof onSelectNode === 'function') onSelectNode(null);
  }, [onSelectNode]);

  // Double-click → fire onNodeDoubleClick(sourceNodeId). Used by V3
  // to trigger the AI Explainer without firing on every casual click.
  const handleNodeDoubleClick = useCallback((_event, node) => {
    if (!node || typeof onNodeDoubleClick !== 'function') return;
    const sourceId = node.data && node.data.sourceNodeId;
    if (!sourceId) return;
    onNodeDoubleClick(sourceId);
  }, [onNodeDoubleClick]);

  // ── Palette drop handling ────────────────────────────────────────
  // Find the canvas-shape connection whose visual midpoint is closest
  // to a flow-space point. Returns { connection, distance } or null.
  // Same algorithm the legacy /canvas uses (midpoint proximity), but
  // the geometry comes from the laid-out rfNodes in flow space — not
  // the legacy Sugiyama x/y on canvas.nodes — so drop-coords and
  // node-coords actually live in the same coordinate system.
  const closestConnectionTo = useCallback((flowPoint) => {
    if (!flowPoint || !payload || !Array.isArray(payload.connections)) return null;
    // Build a sourceNodeId → rfNode lookup. rfNodes' ids are
    // ReactFlow-prefixed (start_…, leaf_…, fork_…) but each carries
    // _v2.sourceNodeId pointing at the canvas-shape id used in
    // payload.connections.{from,to}.
    const sourceToRf = new Map();
    for (const n of rfNodes) {
      const sid = n.data && n.data._v2 && n.data._v2.sourceNodeId;
      if (sid) sourceToRf.set(sid, n);
    }
    let best = null;
    let bestDist = Infinity;
    for (const conn of payload.connections) {
      const fromId = conn.from || conn.source;
      const toId   = conn.to   || conn.target;
      const src = sourceToRf.get(fromId);
      const tgt = sourceToRf.get(toId);
      if (!src || !tgt) continue;
      const sx = (src.position && src.position.x) || 0;
      const sy = (src.position && src.position.y) || 0;
      const tx = (tgt.position && tgt.position.x) || 0;
      const ty = (tgt.position && tgt.position.y) || 0;
      const sw = src.width  || 200;
      const sh = src.height || 80;
      const tw = tgt.width  || 200;
      const th = tgt.height || 80;
      // Midpoint of the visual edge — layout-agnostic. We take the
      // centre of source and the centre of target and average them.
      // Earlier formula computed `mx = ((sx+sw)+tx)/2` which is the
      // X midpoint between source's RIGHT edge and target's LEFT
      // edge — only correct for left-to-right (horizontal) layouts.
      // Canvas is vertical (top-to-bottom) so that biased mx for
      // every edge in the same direction, causing proximity to snap
      // to whichever edge happened to be near the biased point
      // instead of the visually nearest one. Dropping a node "near
      // the End" was instead snapping to the topmost edge near
      // Start. Centre-to-centre fixes both axes for any orientation.
      const sCx = sx + sw / 2;
      const sCy = sy + sh / 2;
      const tCx = tx + tw / 2;
      const tCy = ty + th / 2;
      const mx = (sCx + tCx) / 2;
      const my = (sCy + tCy) / 2;
      const d = Math.hypot(flowPoint.x - mx, flowPoint.y - my);
      if (d < bestDist) {
        bestDist = d;
        best = conn;
      }
    }
    return best ? { connection: best, distance: bestDist } : null;
  }, [payload, rfNodes]);

  // Threshold in flow-space units. Flow space is pre-zoom so this is
  // consistent regardless of how the user has zoomed; tuned to roughly
  // the visual width of one node so the drop is generous but not
  // sloppy enough to swallow drops far from any edge.
  const EDGE_DROP_THRESHOLD = 250;

  const handleDragOver = useCallback((e) => {
    // #66 — matches the legacy /canvas handler: ALWAYS preventDefault
    // on dragOver so the drop event fires. The previous, "defensive"
    // implementation gated preventDefault on dataTransfer.types
    // including 'nodeType', but several browsers expose custom types
    // inconsistently during dragOver (Safari sometimes lower-cases,
    // Chrome sometimes hides custom types entirely until drop). Any
    // miss meant the browser cancelled the drop silently — which is
    // the bug behind "drag-drop on edge does nothing in canvas-v3".
    // The drop handler is the gatekeeper: it inspects getData(
    // 'nodeType') and bails if the dragged thing wasn't ours.
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';

    // Light up the candidate edge so the user can see where the drop
    // will land. Recomputed on every dragOver tick — cheap with the
    // small connection counts we expect (<200).
    try {
      const flowPos = reactFlow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const match = closestConnectionTo(flowPos);
      if (match && match.distance < EDGE_DROP_THRESHOLD) {
        setHoveredEdgeId(match.connection.id || null);
      } else {
        setHoveredEdgeId(null);
      }
    } catch (_err) {
      setHoveredEdgeId(null);
    }
  }, [reactFlow, closestConnectionTo]);

  const handleDragLeave = useCallback(() => {
    setHoveredEdgeId(null);
  }, []);

  const handleDrop = useCallback((e) => {
    if (!e.dataTransfer) return;
    const nodeType = e.dataTransfer.getData('nodeType');
    if (!nodeType) return;
    e.preventDefault();
    // Stop the drop from bubbling up to the host page's legacy
    // `handleCanvasDrop` on the outer wrapper div. Without this, a single
    // palette drop fires BOTH this V2 handler AND the legacy SVG handler
    // (the wrapper still listens for back-compat with the hidden legacy
    // canvas), producing duplicate inserts and conflicting toasts.
    e.stopPropagation();
    setHoveredEdgeId(null);

    let flowPos;
    try {
      flowPos = reactFlow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    } catch (_err) {
      flowPos = { x: 200, y: 200 };
    }
    const match = closestConnectionTo(flowPos);
    const matchedConnectionId = (match && match.distance < EDGE_DROP_THRESHOLD)
      ? (match.connection.id || null)
      : null;

    if (typeof onPaletteDrop === 'function') {
      onPaletteDrop({
        nodeType,
        matchedConnectionId,
        flowPosition: flowPos,
      });
    }
  }, [reactFlow, closestConnectionTo, onPaletteDrop]);

  const handlePaletteActivate = useCallback((item) => {
    if (!item || !item.id) return;
    setFlashRfId(item.id);
    const t = setTimeout(() => setFlashRfId(null), 1400);
    return () => clearTimeout(t);
  }, []);

  const handleCollapseAll = useCallback(() => setExpandedSubworkflows(new Set()), []);

  const breadcrumbItems = useMemo(() => {
    if (!expandedSubworkflows.size || !payload || !Array.isArray(payload.nodes)) return [];
    return [...expandedSubworkflows].map((sourceId) => {
      const src = payload.nodes.find((n) => n && n.id === sourceId);
      return { id: sourceId, label: (src && src.label) || sourceId };
    });
  }, [expandedSubworkflows, payload]);

  // Annotate rfEdges with the hover highlight so the candidate edge
  // pulses while the user drags a palette item over it. We don't
  // mutate rfEdges directly (it's owned by useEdgesState) — instead
  // we project a styled copy at render time. The lookup needs to
  // walk rfEdges and match each edge's data.sourceConnectionId (set
  // by elkToReactFlow) against hoveredEdgeId.
  const renderedEdges = useMemo(() => {
    if (!hoveredEdgeId) return rfEdges;
    return rfEdges.map((e) => {
      const sourceConnId = e.data && (e.data.sourceConnectionId || e.data.connectionId);
      const isHover = sourceConnId === hoveredEdgeId || e.id === hoveredEdgeId;
      if (!isHover) return e;
      return {
        ...e,
        animated: true,
        style: {
          ...(e.style || {}),
          stroke: 'var(--v2-action)',
          strokeWidth: 3,
        },
      };
    });
  }, [rfEdges, hoveredEdgeId]);

  return (
    <div
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
      style={{ position: 'relative', width: '100%', height: '100%', background: V2_PAL.canvas.background }}
    >
      {/* Inline keyframes + diff/flash chrome — same as /canvas-v2. */}
      <style>{`
        @keyframes v2FlashPulse {
          0%   { box-shadow: 0 0 0 0 rgba(226, 74, 58, 0.55); }
          60%  { box-shadow: 0 0 0 10px rgba(226, 74, 58, 0.08); }
          100% { box-shadow: 0 0 0 0 rgba(226, 74, 58, 0); }
        }
        .react-flow__node.v2-flash > div {
          animation: v2FlashPulse 1.35s ease-out;
          border-radius: 12px;
        }
        .react-flow__node.v2-selected > div {
          box-shadow: 0 0 0 3px rgba(226, 74, 58, 0.85), 0 0 0 6px rgba(226, 74, 58, 0.18) !important;
          border-radius: 12px;
        }
        .react-flow__node.v2-diff-added > div {
          box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.85), 0 0 0 6px rgba(22, 163, 74, 0.18);
          border-radius: 12px;
        }
        .react-flow__node.v2-diff-removed > div {
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.85), 0 0 0 6px rgba(220, 38, 38, 0.16);
          border-radius: 12px;
          opacity: 0.55;
          filter: saturate(0.7);
        }
        .react-flow__node.v2-diff-modified > div {
          box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.85), 0 0 0 6px rgba(217, 119, 6, 0.18);
          border-radius: 12px;
        }
        @keyframes v2ExecPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.18); opacity: 0.85; }
        }
        .v2-exec-pulse { animation: v2ExecPulse 1.4s ease-in-out infinite; }
      `}</style>

      {/* Drill-in breadcrumb. */}
      {breadcrumbItems.length > 0 && (
        <div style={{
          position: 'absolute', top: 12, left: 12, zIndex: 10,
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 10px', fontSize: 11,
          background: 'rgba(255,255,255,0.94)',
          border: `1px solid ${V2_PAL.subWorkflow.sync.border}`,
          borderRadius: 999,
          boxShadow: '0 1px 2px rgba(99,102,241,0.18)',
          maxWidth: 'calc(100% - 24px)',
        }}>
          <span style={{
            color: V2_PAL.subWorkflow.sync.accent,
            fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
            fontSize: 9,
          }}>Drilled in</span>
          {breadcrumbItems.map((p) => (
            <button
              key={p.id}
              onClick={() => setExpandedSubworkflows((prev) => {
                const next = new Set(prev);
                next.delete(p.id);
                return next;
              })}
              title={`Collapse ${p.label}`}
              style={{
                padding: '2px 8px', fontSize: 11, fontWeight: 600,
                color: V2_PAL.subWorkflow.sync.text,
                background: V2_PAL.subWorkflow.sync.bg,
                border: `1px solid ${V2_PAL.subWorkflow.sync.border}`,
                borderRadius: 6, cursor: 'pointer',
                maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >{p.label} ×</button>
          ))}
          <button
            onClick={handleCollapseAll}
            style={{
              padding: '2px 8px', fontSize: 10, fontWeight: 500,
              color: V2_STONE[600], background: 'transparent',
              border: `1px dashed ${V2_STONE[300]}`,
              borderRadius: 6, cursor: 'pointer',
            }}
          >collapse all</button>
        </div>
      )}

      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        rfNodes={rfNodes}
        onActivate={handlePaletteActivate}
      />

      {layoutError && (
        <div style={{
          position: 'absolute', top: 12, right: 12, zIndex: 10,
          padding: '6px 10px', fontSize: 11, color: 'var(--v2-accent-rose)',
          background: 'var(--v2-danger-soft)', border: '1px solid var(--v2-accent-rose-soft)', borderRadius: 6,
        }}>Layout error: {layoutError}</div>
      )}

      <ReactFlow
        nodes={rfNodes}
        edges={renderedEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onPaneClick={handlePaneClick}
        // Canvas Round 1 Patch A — persist node drag. ReactFlow fires
        // `onNodeDragStop` once when the user releases the mouse after
        // dragging a node. We extract the canvas-shape `sourceNodeId`
        // from the rfNode's data envelope (lint pass already stamps it
        // there) and hand the new flow-coords up so the host can call
        // canvas.updateNode with `manualPosition: true`. The next layout
        // pass will respect the pin (useCanvas:1037 — "Pinned by user:
        // preserve their coordinates verbatim").
        onNodeDragStop={(_event, rfNode) => {
          if (typeof onNodeMove !== 'function') return;
          const sourceNodeId = (rfNode?.data && rfNode.data.sourceNodeId) || rfNode?.id;
          const pos = rfNode?.position;
          if (!sourceNodeId || !pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;
          onNodeMove({ sourceNodeId, x: pos.x, y: pos.y });
        }}
        // Canvas Round 1 Patch B — restore manual edge drawing. ReactFlow
        // hands us its own rfNode ids; we resolve them back to canvas
        // sourceNodeIds via rfNodes lookup (lint pass already stamped
        // data.sourceNodeId on every rfNode). We guard against:
        //   - missing handler (read-only viewer)
        //   - missing source/target (handle drag aborted)
        //   - self-loops (sourceNodeId === targetNodeId; useCanvas
        //     would reject these anyway via canConnect but bailing
        //     early skips the round-trip)
        onConnect={(connection) => {
          if (typeof onConnectEdge !== 'function') return;
          if (!connection?.source || !connection?.target) return;
          const srcRf = rfNodes.find((n) => n.id === connection.source);
          const tgtRf = rfNodes.find((n) => n.id === connection.target);
          const sourceNodeId = (srcRf?.data && srcRf.data.sourceNodeId) || connection.source;
          const targetNodeId = (tgtRf?.data && tgtRf.data.sourceNodeId) || connection.target;
          if (!sourceNodeId || !targetNodeId || sourceNodeId === targetNodeId) return;
          onConnectEdge({
            sourceNodeId,
            targetNodeId,
            sourceHandle: connection.sourceHandle || null,
            targetHandle: connection.targetHandle || null,
          });
        }}
        nodeTypes={V2_NODE_TYPES}
        edgeTypes={V2_EDGE_TYPES}
        fitView
        // `maxZoom: 1` clamps fitView so a sparse graph (e.g. fresh
        // workflow with just Start → Webhook → End) doesn't get
        // zoomed in to fill the whole viewport. Without the cap, 3-node
        // workflows render with each node taking up ~⅓ of the canvas
        // height, which feels broken even though the graph is correct.
        // `minZoom: 0.1` is the inverse defense for huge GEL DAGs.
        fitViewOptions={{ padding: 0.2, maxZoom: 1, minZoom: 0.1 }}
        // ReactFlow's default zoom range is 0.5–2. fitView for large
        // workflows can land below 0.5 — at which point clicking the
        // "+" zoom button clamps the user back UP to 0.5, looking
        // like the canvas "jumped to 50%". Mirror the fitView range
        // here so manual zoom and fit-to-view share the same bounds:
        // 0.1 floor lets large GEL DAGs stay readable, 2.5 ceiling
        // gives operators room to inspect dense node clusters.
        minZoom={0.1}
        maxZoom={2.5}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{ style: { strokeWidth: 1.5, stroke: 'var(--v2-text-secondary)' } }}
        // ReactFlow's pane double-click zooms by default; a node
        // double-click bubbles to the pane and triggers the same
        // zoom, hiding the AI Explainer trigger we wired in. Disable
        // zoom-on-double-click so node double-click is purely the
        // explainer trigger; users still have +/- and scroll-wheel
        // zoom via the Controls toolbar.
        zoomOnDoubleClick={false}
      >
        <svg style={{ position: 'absolute', width: 0, height: 0 }}>
          <defs>
            <marker id="v2-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" style={{ fill: 'var(--v2-text-secondary)' }} />
            </marker>
            <marker id="v2-arrow-loop" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" style={{ fill: 'var(--v2-warning)' }} />
            </marker>
            {/* G267b — cognitive tool bus (loop ⇄ tool). `auto-start-reverse` lets the SAME
                marker serve as markerStart (pointing back at the loop) and markerEnd
                (pointing at the tool), so one dashed edge carries both directions. Cyan
                matches the `availability` stroke in elkToReactFlow.variantEdgeStyle. */}
            <marker id="v2-arrow-cognitive" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" style={{ fill: '#0891b2' }} />
            </marker>
          </defs>
        </svg>
        <Background color={V2_STONE[200]} gap={24} />
        {/* Controls toolbar — horizontal layout pinned bottom-centre.
            ReactFlow's default is vertical bottom-left, which got clipped
            in execution view (the canvas region collapses to 55% height
            and the vertical strip overflowed under the bottom timeline
            panel). Horizontal + bottom-center keeps it visible in both
            design and execution layouts; z-index 10 sits above any
            overlay so it's always reachable. The transform/centre is
            applied via a className the inline <style> below targets,
            since @xyflow/react doesn't ship a "bottom-center" position. */}
        <Controls
          showInteractive={false}
          orientation="horizontal"
          position="bottom-left"
          className="v2-controls-bottom-center"
        >
          {/* Leading "+" — opens the global Task Picker. Rendered as the
              first <ControlButton/> child so it sits leftmost in the
              horizontal toolbar (ReactFlow renders custom children before
              the built-in zoom-in/zoom-out/fit/lock buttons). A thin right
              divider visually groups it as a distinct "add" action,
              separate from the "view" actions that follow. We hide it
              entirely when no host callback is wired so other consumers
              of V2CanvasView (the demo flow, /canvas-v2) don't get a dead
              button. */}
          {onOpenPicker && (
            <ControlButton
              onClick={onOpenPicker}
              title="Add task (⌘E)"
              aria-label="Open task picker"
              className="v2-controls-add-btn"
            >
              {/* Pill-shaped "+ Task" so the picker entry point is
                  unmistakably distinct from the bare-icon zoom-in "+".
                  Without the label, two identical "+" icons end up in the
                  same toolbar and users (and tests) can't tell which one
                  opens the picker. The label is wrapped in a span so we
                  control its layout via CSS without touching ReactFlow's
                  default ControlButton flex rules. */}
              <span className="v2-controls-add-btn-inner">
                <Plus size={14} strokeWidth={2.5} />
                <span className="v2-controls-add-btn-label">Task</span>
              </span>
            </ControlButton>
          )}
        </Controls>
        <style>{`
          .react-flow__controls.v2-controls-bottom-center {
            left: 50%;
            transform: translateX(-50%);
            bottom: 12px;
            z-index: 10;
            display: flex;
            flex-direction: row;
            gap: 0;
            background: var(--v2-bg-surface);
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(15, 23, 42, 0.14);
            padding: 4px;
          }
          .react-flow__controls.v2-controls-bottom-center .react-flow__controls-button {
            width: 30px;
            height: 30px;
            border: none;
            border-radius: 6px;
            background: transparent;
          }
          .react-flow__controls.v2-controls-bottom-center .react-flow__controls-button:hover {
            background: var(--v2-bg-muted);
          }
          /* Group the leading "+" visually: subtle right divider to read
             as "Add  |  Zoom -  Zoom +  Fit", and a slight emphasis tint
             so it's the first thing the eye lands on. Sky-50 hover keeps
             it on-brand with our picker accent.

             ReactFlow's Controls renders its built-in zoom/fit buttons
             before any custom ControlButton children, so a child lands
             rightmost in DOM order. order:-1 flips it to leftmost
             visually via flex order while leaving DOM/tab order intact
             (React DOM = source of truth for screen-readers; visual order
             = source of truth for sighted users). Both audiences get the
             "+" first, both get a logical narrative. */
          .react-flow__controls.v2-controls-bottom-center .v2-controls-add-btn {
            order: -1;
            width: auto !important;
            min-width: auto !important;
            padding: 0 10px !important;
            margin-right: 6px;
            border-right: 1px solid var(--v2-border-default) !important;
            border-radius: 6px !important;
            /* Fix G52: was amber var(--v2-warning) (residue from the earlier
             * brand-red sweep — Round 6 G46). The "+ Task" pill is the
             * primary picker entry point on the canvas, so it gets the
             * v2 deep-teal action color, not warning amber. */
            background: var(--v2-action);
            color: var(--v2-bg-surface);
            font-weight: 500;
          }
          .react-flow__controls.v2-controls-bottom-center .v2-controls-add-btn:hover {
            background: var(--v2-action-strong);
          }
          .v2-controls-add-btn-inner {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            color: var(--v2-bg-surface);
          }
          .v2-controls-add-btn-inner .v2-controls-add-btn-label {
            font-size: 12px;
            font-weight: 500;
            letter-spacing: 0.01em;
            line-height: 1;
          }
          /* lucide icons inherit currentColor when no color prop is set */
          .v2-controls-add-btn-inner svg {
            display: block;
            stroke: currentColor;
          }
        `}</style>
        {showMinimap && <MiniMap pannable zoomable maskColor="rgba(15,23,42,0.08)" />}
      </ReactFlow>
    </div>
  );
}

// Wrapper that ensures a ReactFlowProvider is in scope. If a host is
// already wrapped in a provider it can use V2CanvasViewInner directly to
// avoid nesting; the default export is safer for one-off embeds.
export default function V2CanvasView(props) {
  return (
    <ReactFlowProvider>
      <V2CanvasViewInner {...props} />
    </ReactFlowProvider>
  );
}
